import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

// Not asked for anyone yet
const NOBODY = {}

/**
 * Loads a user's files (on mount, when the user changes, and on reload). The store keeps one
 * list for whoever was asked for last, so it only counts once it is this user's list: until
 * then it is loading, and nobody else's files are ever shown.
 */
export const useUserFiles = (
  getUserFilesAction: (payload: { userId?: string }) => void,
  userId?: string
) => {
  const listUserId = useSelector((state: any) => state.common.userFilesUserId)
  const isFetching = useSelector((state: any) => !!state.common.userFilesLoading)
  const hasFailed = useSelector((state: any) => !!state.common.userFilesError)
  // Who this panel last asked for: a list asked for elsewhere, before it opened, is not its own
  const askedFor = useRef<unknown>(NOBODY)

  const reload = useCallback(() => {
    askedFor.current = userId
    getUserFilesAction({ userId })
  }, [getUserFilesAction, userId])

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const isOwnList = askedFor.current === userId && listUserId === userId
  return {
    reload,
    isLoading: !isOwnList || isFetching,
    isError: isOwnList && !isFetching && hasFailed,
  }
}

interface IUseUploadUserFiles {
  userId?: string
  /** The files shown now, to tell when the uploaded ones are in the list */
  files?: { id?: string | null }[] | null
  saveUserFileAction: (payload: {
    generationNotification: boolean
    userId?: string
    files: File[]
  }) => void
}

/**
 * Uploads the chosen files and saves them to the user's files. The upload runs and reports
 * on its own (see the common saga), so leaving the page never loses it. Busy while an upload
 * for this user runs, and returns the ids of the files it added once they are in the list.
 */
export const useUploadUserFiles = ({ userId, files, saveUserFileAction }: IUseUploadUserFiles) => {
  const isUploading = useSelector(
    (state: any) => !!userId && state.common.userFilesUploading?.[userId] > 0
  )
  const uploaded = useSelector((state: any) => state.common.userFilesUploaded)
  const [newIds, setNewIds] = useState<string[]>([])
  const [recentIds, setRecentIds] = useState<string[]>([])

  // Saved while this panel is open: those are the new files (an earlier upload is not). Uploads
  // that run side by side can land together, so each one adds to the new files.
  const seen = useRef(uploaded)
  useEffect(() => {
    if (uploaded === seen.current) return
    seen.current = uploaded
    if (uploaded?.userId === userId) setNewIds((ids) => [...ids, ...uploaded.ids])
  }, [uploaded, userId])

  // Another user's panel: the last user's new files are not new here
  useEffect(() => {
    setNewIds((ids) => (ids.length ? [] : ids))
    setRecentIds((ids) => (ids.length ? [] : ids))
  }, [userId])

  // Highlighted once they are in the list (it reloads after the save)
  useEffect(() => {
    const landed = newIds.filter((id) => (files || []).some((file) => file.id === id))
    if (!landed.length) return
    setRecentIds(landed)
    setNewIds((ids) => ids.filter((id) => !landed.includes(id)))
  }, [newIds, files])

  const upload = useCallback(
    (picked: File[]) => {
      if (!picked.length) return
      saveUserFileAction({ generationNotification: true, userId, files: picked })
    },
    [saveUserFileAction, userId]
  )

  return { upload, isUploading, recentIds }
}
